import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import './i18n';
import './App.css';
import './styles/PageShell.css';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Carpools from './pages/Carpools';
import CarpoolDetails from './pages/CarpoolDetails';
import CarpoolDetailsModern from './pages/CarpoolDetailsModern';
import CreateCarpool from './pages/CreateCarpool';
import Children from './pages/Children';
import Schedule from './pages/Schedule';
import Tracking from './pages/Tracking';
import Invitations from './pages/Invitations';
import Profile from './pages/Profile';
import JoinCarpool from './pages/JoinCarpool';
import UiPrototype from './pages/UiPrototype';
import CarpoolDesignPrototype from './pages/CarpoolDesignPrototype';

// Admin
import AdminLayout from './admin/AdminLayout';
import AdminDashboard from './admin/pages/AdminDashboard';
import AdminUsers from './admin/pages/AdminUsers';
import AdminCarpools from './admin/pages/AdminCarpools';
import AdminRides from './admin/pages/AdminRides';
import AdminSettings from './admin/pages/AdminSettings';

// Components
import Sidebar from './components/Sidebar';
import AppChrome from './components/AppChrome';
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';
import Loading from './components/Loading';

// Layout wrapper for regular pages with sidebar
const AppLayout = ({ children }) => (
  <div className="app-layout">
    <Sidebar />
    <main className="main-content">
      <AppChrome />
      <div className="main-content-inner">{children}</div>
    </main>
  </div>
);

function AppContent() {
  const { loading } = useAuth();

  if (loading) {
    return <Loading />;
  }

  return (
    <Router>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/join/:token" element={<JoinCarpool />} />
        <Route path="/ui-prototype" element={<UiPrototype />} />
        <Route path="/design-prototype" element={<CarpoolDesignPrototype />} />

        {/* Admin routes */}
        <Route 
          path="/admin" 
          element={
            <AdminRoute>
              <AdminLayout>
                <AdminDashboard />
              </AdminLayout>
            </AdminRoute>
          } 
        />
        <Route 
          path="/admin/users" 
          element={
            <AdminRoute>
              <AdminLayout>
                <AdminUsers />
              </AdminLayout>
            </AdminRoute>
          } 
        />
        <Route 
          path="/admin/carpools" 
          element={
            <AdminRoute>
              <AdminLayout>
                <AdminCarpools />
              </AdminLayout>
            </AdminRoute>
          } 
        />
        <Route 
          path="/admin/rides" 
          element={
            <AdminRoute>
              <AdminLayout>
                <AdminRides />
              </AdminLayout>
            </AdminRoute>
          } 
        />
        <Route 
          path="/admin/settings" 
          element={
            <AdminRoute>
              <AdminLayout>
                <AdminSettings />
              </AdminLayout>
            </AdminRoute>
          } 
        />

        {/* Private routes */}
        <Route path="/" element={
          <AppLayout>
            <PrivateRoute><Dashboard /></PrivateRoute>
          </AppLayout>
        } />
        <Route path="/dashboard" element={
          <AppLayout>
            <PrivateRoute><Dashboard /></PrivateRoute>
          </AppLayout>
        } />
        <Route path="/carpools" element={
          <AppLayout>
            <PrivateRoute><Carpools /></PrivateRoute>
          </AppLayout>
        } />
        <Route path="/carpools/new" element={
          <AppLayout>
            <PrivateRoute><CreateCarpool /></PrivateRoute>
          </AppLayout>
        } />
        <Route path="/carpools/:id" element={
          <AppLayout>
            <PrivateRoute><CarpoolDetails /></PrivateRoute>
          </AppLayout>
        } />
        <Route path="/prototype-details/:id" element={
          <AppLayout>
            <PrivateRoute><CarpoolDetailsModern /></PrivateRoute>
          </AppLayout>
        } />
        <Route path="/children" element={
          <AppLayout>
            <PrivateRoute><Children /></PrivateRoute>
          </AppLayout>
        } />
        <Route path="/schedule/:carpoolId" element={
          <AppLayout>
            <PrivateRoute><Schedule /></PrivateRoute>
          </AppLayout>
        } />
        <Route path="/tracking/:carpoolId" element={
          <AppLayout>
            <PrivateRoute><Tracking /></PrivateRoute>
          </AppLayout>
        } />
        <Route path="/invitations" element={
          <AppLayout>
            <PrivateRoute><Invitations /></PrivateRoute>
          </AppLayout>
        } />
        <Route path="/profile" element={
          <AppLayout>
            <PrivateRoute><Profile /></PrivateRoute>
          </AppLayout>
        } />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <Toaster
        position="bottom-center"
        toastOptions={{
          duration: 6000,
          className: 'app-toast',
          success: {
            className: 'app-toast app-toast--success'
          },
          error: {
            className: 'app-toast app-toast--error'
          }
        }}
      />
      <AppContent />
    </AuthProvider>
  );
}

export default App;
